export const formatDate = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)
    return date.toLocaleDateString("en-US", { weekday: "long" });
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

export const handleCopy = (message) => {
  navigator.clipboard.writeText(message).then(
    () => {
      console.log("Message copied to clipboard");
    },
    (err) => {
      console.error("Could not copy text: ", err);
    }
  );
};

export const buildMessageHistory = (messages) => {
  return messages.map(message => ({
    role: message.isAI ? "assistant" : "user",
    content: message.message
  }));
};

export const getActiveSession = (chatSessions, activeSession) => {
  if (!chatSessions || !activeSession) {
    // Return a default session if no active session exists
    return {
      id: null,
      messages: [],
      bot: 'askMN',
      lastActive: Date.now(),
      snippet: 'New Chat'
    };
  }

  const session = chatSessions.find((session) => session.id === activeSession);
  if (!session) {
    console.error('A chat session with id matching active session was not found');
    // Return a default session if the active session is not found
    return {
      id: null,
      messages: [],
      bot: 'askMN',
      lastActive: Date.now(),
      snippet: 'New Chat'
    };
  }
  return session;
};

export const getIpAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Error fetching IP address:", error);
    return null;
  }
};

// for non-logged in users to try
export const getNextMessage = (timestamps) => {
  if (timestamps.length === 0) {
    return "";
  }

  const oldestTimestamp = Math.min(...timestamps);
  const expirationDate = new Date(oldestTimestamp + 24 * 60 * 60 * 1000);

  let hours = expirationDate.getHours();
  const period = hours >= 12 ? "PM" : "AM";

  // Convert from 24-hour to 12-hour format
  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;

  return ` until ${hours}${period}`;
};

export const getTimeSortedChatSessions = (chatSessions) => {
  const now = new Date();
  const oneDay = 24 * 60 * 60 * 1000;
  const oneWeek = 7 * oneDay;
  const thirtyDays = 30 * oneDay;

  // Helper to start a day at midnight
  const startOfDay = (date) => new Date(date.setHours(0, 0, 0, 0)).getTime();

  // Create reference times
  const today = startOfDay(new Date());
  const yesterday = today - oneDay;
  const sevenDaysAgo = today - oneWeek;
  const thirtyDaysAgo = today - thirtyDays;

  // Organize sessions
  const sortedSessions = {
    Today: [],
    Yesterday: [],
    "Previous 7 Days": [],
    "Previous 30 Days": []
  };

  // Initialize empty month and year groups as needed
  let currentMonth = now.getMonth();
  let currentYear = now.getFullYear();
  let monthGroups = {};
  let yearGroups = {};

  // Sort each chat session into the correct group
  chatSessions.forEach((session) => {
    const lastActive = session.lastActive || session.id;
    const sessionDate = new Date(lastActive);

    // Today
    if (lastActive >= today) {
      sortedSessions.Today.push(session);
    }
    // Yesterday
    else if (lastActive >= yesterday) {
      sortedSessions.Yesterday.push(session);
    }
    // Previous 7 Days (excluding today and yesterday)
    else if (lastActive >= sevenDaysAgo) {
      sortedSessions["Previous 7 Days"].push(session);
    }
    // Previous 30 Days (excluding the last 7 days)
    else if (lastActive >= thirtyDaysAgo) {
      sortedSessions["Previous 30 Days"].push(session);
    }
    // Current Month, but beyond 30 days ago
    else if (
      sessionDate.getFullYear() === currentYear &&
      sessionDate.getMonth() === currentMonth
    ) {
      const monthName = sessionDate.toLocaleString("default", {
        month: "long"
      });
      monthGroups[monthName] = monthGroups[monthName] || [];
      monthGroups[monthName].push(session);
    }
    // Previous months up to January in the current year
    else if (sessionDate.getFullYear() === currentYear) {
      const monthName = sessionDate.toLocaleString("default", {
        month: "long"
      });
      monthGroups[monthName] = monthGroups[monthName] || [];
      monthGroups[monthName].push(session);
    }
    // Previous years
    else {
      const yearName = sessionDate.getFullYear();
      yearGroups[yearName] = yearGroups[yearName] || [];
      yearGroups[yearName].push(session);
    }
  });

  // Merge month groups in reverse order for current year
  const monthNames = Object.keys(monthGroups).reverse();
  monthNames.forEach((month) => {
    sortedSessions[month] = monthGroups[month];
  });

  // Merge year groups in reverse order
  const yearNames = Object.keys(yearGroups).sort((a, b) => b - a);
  yearNames.forEach((year) => {
    sortedSessions[year] = yearGroups[year];
  });

  return sortedSessions;
};
